import { TechnicianCutDetails, LabeledAmount } from '@/types';
import { withBackNavigation } from '@/lib/withBackNavigation';
import type { ModalLayer } from '@/lib/layerManager';
import { X, DollarSign, ArrowUpCircle, Info, RefreshCw } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useCompanyTimezone } from '@/hooks/useGlobalCompany';
import { formatInstantInCompanyTimeZone } from '@/lib/dateUtils';

interface TechnicianCutDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    details?: TechnicianCutDetails;
    technicianName?: string;
    layer?: ModalLayer;
}

function TechnicianCutDetailsModalBase({ isOpen, onClose, details, technicianName, layer }: TechnicianCutDetailsModalProps) {
    if (!isOpen || !details) return null;

    const companyTz = useCompanyTimezone();

    // Format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    };

    // Format date
    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return 'N/A';
        return formatInstantInCompanyTimeZone(dateString, companyTz);
    };

    return createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4" style={layer?.style}>
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <DollarSign className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Technician Cut Details</h2>
                            {technicianName && (
                                <p className="text-sm text-gray-500">{technicianName}</p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {/* Override Notice */}
                    {details.is_override && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <Info className="h-5 w-5 text-orange-500 mt-0.5" />
                                <div>
                                    <h4 className="font-medium text-orange-800">Manual Override</h4>
                                    <p className="text-sm text-orange-700 mt-1">
                                        The cut amount was manually set. Reimbursements are not affected.
                                    </p>
                                    {details.override_reason && (
                                        <p className="text-sm text-orange-700 mt-2">
                                            <strong>Reason:</strong> {details.override_reason}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Pay Base Breakdown */}
                    {!details.is_override && (
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Pay Base Calculation</h4>
                            <div className="space-y-2">
                                {/* Job Total (with tax) - always show this first */}
                                <div className="flex items-center justify-between py-1">
                                    <span className="text-sm text-gray-600">Job Total</span>
                                    <span className="text-sm font-medium text-gray-900">
                                        {formatCurrency(details.job_total || 0)}
                                    </span>
                                </div>

                                {/* Tax deduction - only show for "Labor without tax" plan */}
                                {details.pay_base_label?.toLowerCase().includes('without') && (details.tax_amount || 0) > 0 && (
                                    <div className="flex items-center justify-between py-1">
                                        <span className="text-sm text-gray-600">Tax</span>
                                        <span className="text-sm font-medium text-red-600">
                                            -{formatCurrency(details.tax_amount)}
                                        </span>
                                    </div>
                                )}
                                {/* Payment Fee Deductions - per-method breakdown or legacy single amount */}
                                {details.payment_fee_deductions && details.payment_fee_deductions.length > 0 ? (
                                    details.payment_fee_deductions.map((fee: LabeledAmount, idx: number) => (
                                        <div key={`fee-${idx}`} className="flex items-center justify-between py-1">
                                            <span className="text-sm text-gray-600">{fee.label}</span>
                                            <span className="text-sm font-medium text-red-600">
                                                -{formatCurrency(fee.amount)}
                                            </span>
                                        </div>
                                    ))
                                ) : (details.payment_fees_deduction || 0) > 0 ? (
                                    <div className="flex items-center justify-between py-1">
                                        <span className="text-sm text-gray-600">Payment Fees</span>
                                        <span className="text-sm font-medium text-red-600">
                                            -{formatCurrency(details.payment_fees_deduction!)}
                                        </span>
                                    </div>
                                ) : null}

                                {/* Company Parts (only show if > 0) */}
                                {(details.company_parts_amount || 0) > 0 && (
                                    <div className="flex items-center justify-between py-1">
                                        <span className="text-sm text-gray-600">Company Parts</span>
                                        <span className="text-sm font-medium text-red-600">
                                            -{formatCurrency(details.company_parts_amount)}
                                        </span>
                                    </div>
                                )}

                                {/* Technician Parts (only show if > 0) */}
                                {(details.technician_parts_amount || 0) > 0 && (
                                    <div className="flex items-center justify-between py-1">
                                        <span className="text-sm text-gray-600">Technician Parts</span>
                                        <span className="text-sm font-medium text-red-600">
                                            -{formatCurrency(details.technician_parts_amount)}
                                        </span>
                                    </div>
                                )}

                                {/* Divider */}
                                <div className="border-t border-gray-200 my-2" />

                                {/* Pay Base Result */}
                                <div className="flex items-center justify-between py-1">
                                    <span className="text-sm font-medium text-gray-700">
                                        Pay Base ({details.pay_base_label})
                                    </span>
                                    <span className="text-base font-semibold text-gray-900">
                                        {formatCurrency(details.pay_base_amount)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Earnings (from compensation plan) */}
                    {details.earnings.length > 0 && (
                        <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <ArrowUpCircle className="h-4 w-4 text-green-500" />
                                Cut (from Plan)
                            </h4>
                            <div className="space-y-2">
                                {details.earnings.map((item: LabeledAmount, index: number) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between py-2 px-3 bg-green-50 rounded-md"
                                    >
                                        <span className="text-sm text-gray-700">{item.label}</span>
                                        <span className="text-sm font-medium text-green-700">
                                            {formatCurrency(item.amount)}
                                        </span>
                                    </div>
                                ))}
                                <div className="flex items-center justify-between py-2 px-3 border-t border-green-200">
                                    <span className="text-sm font-medium text-gray-700">Technician Cut</span>
                                    <span className="text-sm font-bold text-green-700">
                                        {formatCurrency(details.final_cut)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Reimbursements */}
                    {details.reimbursements && details.reimbursements.length > 0 && (
                        <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <RefreshCw className="h-4 w-4 text-blue-500" />
                                Reimbursements
                            </h4>
                            <div className="space-y-2">
                                {details.reimbursements.map((item: LabeledAmount, index: number) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between py-2 px-3 bg-blue-50 rounded-md"
                                    >
                                        <span className="text-sm text-gray-700">{item.label}</span>
                                        <span className="text-sm font-medium text-blue-700">
                                            {formatCurrency(item.amount)}
                                        </span>
                                    </div>
                                ))}
                                <div className="flex items-center justify-between py-2 px-3 border-t border-blue-200">
                                    <span className="text-sm font-medium text-gray-700">Total Reimbursement</span>
                                    <span className="text-sm font-bold text-blue-700">
                                        {formatCurrency(details.technician_reimbursement)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Total Compensation */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <span className="text-base font-medium text-gray-900">Total Compensation</span>
                            <span className="text-xl font-bold text-green-700">
                                {formatCurrency(details.technician_compensation)}
                            </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            Cut ({formatCurrency(details.final_cut)}) + Reimbursement ({formatCurrency(details.technician_reimbursement)})
                        </p>
                    </div>

                    {/* Technician Balance */}
                    {details.technician_balance !== undefined && (
                        <div className={`border rounded-lg p-4 ${details.technician_balance >= 0
                            ? 'bg-amber-50 border-amber-200'
                            : 'bg-green-50 border-green-200'
                            }`}>
                            <div className="flex items-center justify-between">
                                <span className="text-base font-medium text-gray-900">Settlement Balance</span>
                                <span className={`text-xl font-bold ${details.technician_balance >= 0 ? 'text-amber-600' : 'text-green-700'
                                    }`}>
                                    {formatCurrency(Math.abs(details.technician_balance))}
                                </span>
                            </div>
                            <p className="text-xs text-gray-600 mt-2">
                                {details.technician_balance >= 0
                                    ? 'Company owes technician'
                                    : 'Technician owes company'}
                            </p>
                            {details.net_collected_by_technician > 0 && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Net collected by tech: {formatCurrency(details.net_collected_by_technician)}
                                    {details.fees_paid_by_technician > 0 && (
                                        <span> (after {formatCurrency(details.fees_paid_by_technician)} in fees)</span>
                                    )}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Calculated timestamp */}
                    {details.calculated_at && (
                        <div className="text-xs text-gray-500 text-center">
                            Calculated on {formatDate(details.calculated_at)}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
export const TechnicianCutDetailsModal = withBackNavigation(TechnicianCutDetailsModalBase);